import { AfterViewChecked, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isEmpty, omitBy, pick, some } from 'lodash-es';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { parsePhoneNumber } from 'libphonenumber-js';
import { TitleCasePipe } from '@angular/common';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { formRadioGroup, formRow, RktFormModule } from '@rocketfinancialcorp/rocket-ui/form';
import { authFeature } from '@store';
import { BusinessAccountService } from '@shared/services';
import { AddressAddComponent, FormSelectFieldComponent } from '@shared/components';
import { ErrorUtils, formatLegalId, phoneNumberFormat } from '@shared/utils';
import { Address, BusinessAccountAddressesData, BusinessAccountItem, CreateBusinessAccountParams, FormModel } from '@shared/models';
import {
  createBusinessAccountAdditionalFields,
  businessAccountContactPersonFields,
  createBusinessAccountFields,
} from '../business-account-fields';

@Component({
  selector: 'app-add-business-account',
  templateUrl: './add-business-account.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RktFormModule,
    AddressAddComponent,
    RktButtonDirective,
    TitleCasePipe,
    FormSelectFieldComponent,
  ],
})
export class AddBusinessAccountComponent implements AfterViewChecked {
  store = inject(Store);

  accountType!: string;

  businessAccountTypeForm = new FormGroup({});

  businessAccountForm = new FormGroup({});

  businessAccountAdditionalForm = new FormGroup({});

  isAddressFormInvalid = true;

  isLoading = false;

  formSubmitError!: string;

  createBusinessAccountFields = createBusinessAccountFields;

  industries$ = this.store.select(authFeature.selectIndustries);

  createBusinessAccountAdditionalFields = createBusinessAccountAdditionalFields(this.industries$);

  createBusinessAccountContactPersonFields = businessAccountContactPersonFields;

  businessAccountTypeFields = [
    formRow([
      formRadioGroup({
        key: 'accountType',
        label: 'Account Type',
        props: {
          options: [
            { value: 'CHILD', label: 'Child Business Account' },
            { value: 'PARENT', label: 'Parent Business Account' },
          ],
        },
      }),
    ]),
  ];

  formModel: FormModel<CreateBusinessAccountParams> = {
    accountType: 'CHILD',
    authProvider: 'GCP_IDENTITY_PLATFORM',
    authInstanceName: 'gcp-default',
  };

  parentBusinessAccountListItems: { label: string; value: string }[] = [];

  parentBusinessAccountListLoading = false;

  parentBusinessAccountId?: string;

  parentBusinessAccountListHasNext = true;

  parentBusinessAccountListPageNumber = 0;

  parentBusinessAccountListPageNumber$ = new Subject<number>();

  get addresses() {
    return pick(this.formModel, 'physicalAddresses');
  }

  addressFormValidity: Record<string, boolean> = {
    PHYSICAL: false,
    MAILING: false,
    SHIPPING: false,
  };

  businessAccountAddresses: BusinessAccountAddressesData = {};

  get isSomeAddressFormInvalid(): boolean {
    return some(this.addressFormValidity, (isValid: boolean) => !isValid);
  }

  private destroy$ = new Subject<void>();

  onTypeModelChange(data: unknown) {
    const { accountType, authInstanceName, authProvider } = data as CreateBusinessAccountParams;
    this.formModel = {
      ...this.formModel,
      accountType,
      authInstanceName: accountType === 'CHILD' ? undefined : authInstanceName,
      authProvider: accountType === 'CHILD' ? undefined : authProvider,
    };
  }

  modelChange(data: unknown) {
    const { legalId } = data as CreateBusinessAccountParams;
    if (legalId) {
      this.formModel = {
        ...this.formModel,
        legalId,
      };
    }
  }

  constructor(
    public activeModal: ActiveModal,
    private ref: ChangeDetectorRef,
    private businessAccountService: BusinessAccountService,
  ) {
    this.parentBusinessAccountListPageNumber$
      .pipe(
        switchMap((pageNumber) => {
          this.parentBusinessAccountListLoading = true;

          return this.businessAccountService.getBusinessAccounts({
            page: pageNumber,
            size: 10,
            activeFilters: { listParentsOnly: 'true' },
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (financialAccounts) => {
          this.parentBusinessAccountListItems = this.mapParentBusinessAccountListItems(financialAccounts.items, financialAccounts.hasNext!);
          this.parentBusinessAccountListLoading = false;
        },
        error: () => {
          this.parentBusinessAccountListLoading = false;
        },
      });
    this.parentBusinessAccountListPageNumber$.next(0);
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  setAddresses(addresses: Address[], addressType: keyof BusinessAccountAddressesData): void {
    this.businessAccountAddresses[addressType] = addresses;
  }

  onSubmit(): void {
    this.businessAccountForm.markAllAsTouched();
    this.businessAccountAdditionalForm.markAllAsTouched();

    if (this.businessAccountForm.invalid || this.isSomeAddressFormInvalid) {
      return;
    }

    const {
      phoneNumber = '',
      legalId,
      accountType,
      primaryContactPhoneNumber,
      primaryContactLastName,
      primaryContactFirstName,
      primaryContactMiddleName,
      primaryContactEmailAddress,
      authProvider,
      authInstanceName,
      ...rest
    } = this.formModel;

    const { physicalAddresses = [], mailingAddresses = [], shippingAddresses = [] } = this.businessAccountAddresses;

    if (
      !phoneNumber ||
      this.businessAccountForm.invalid ||
      this.businessAccountAdditionalForm.invalid ||
      (accountType === 'CHILD' && !this.parentBusinessAccountId)
    ) {
      return;
    }

    this.isLoading = true;

    this.formSubmitError = '';

    const businessAccountPrimaryContact = omitBy(
      {
        firstName: primaryContactFirstName!,
        lastName: primaryContactLastName!,
        middleName: isEmpty(primaryContactMiddleName) ? undefined : primaryContactMiddleName,
        primaryEmail: isEmpty(primaryContactEmailAddress) ? undefined : { value: primaryContactEmailAddress },
        primaryPhoneNumber: isEmpty(primaryContactPhoneNumber) ? undefined : { number: phoneNumberFormat(primaryContactPhoneNumber)! },
      },
      isEmpty,
    );

    const businessAccountData = {
      ...rest,
      authProvider: accountType === 'PARENT' ? authProvider : undefined,
      authInstanceName: accountType === 'PARENT' ? authInstanceName : undefined,
      phoneNumber: parsePhoneNumber(phoneNumber).number.toString(),
      legalId: formatLegalId(legalId),
      addresses: [...physicalAddresses, ...mailingAddresses, ...shippingAddresses],
      contacts: Object.keys(businessAccountPrimaryContact).length ? [businessAccountPrimaryContact] : undefined,
    };

    const parentId = accountType === 'CHILD' ? this.parentBusinessAccountId : undefined;
    const createURL = accountType === 'CHILD' ? `/v1/business-accounts/${parentId}/children` : `/v1/business-accounts`;

    this.businessAccountService.createBusinessAccount(createURL, businessAccountData).subscribe({
      next: (createResponse) => {
        this.activeModal.close(createResponse);
        this.businessAccountForm.reset();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.formSubmitError = ErrorUtils.parseExtendedError(error);
      },
    });
  }

  onParentBusinessAccountSelectScrollToEnd(): void {
    if (this.parentBusinessAccountListLoading || !this.parentBusinessAccountListHasNext) {
      return;
    }
    const pageNumber = this.parentBusinessAccountListPageNumber + 1;
    this.parentBusinessAccountListPageNumber = pageNumber;
    this.parentBusinessAccountListPageNumber$.next(pageNumber);
  }

  mapParentBusinessAccountListItems(businessAccounts: BusinessAccountItem[], hasNext: boolean): { label: string; value: string }[] {
    const businessAccountItems = businessAccounts.map((businessAccount) => ({
      label: `${businessAccount.name} (${businessAccount.name})`,
      value: businessAccount.id,
    }));
    this.parentBusinessAccountListHasNext = hasNext;

    return [...this.parentBusinessAccountListItems, ...businessAccountItems];
  }
}
